import { api } from "@/lib/api";
import {
	artDotColor,
	cn,
	formatCurrency,
	formatDate,
	relativeTime,
	statusColor,
	statusEmoji,
} from "@/lib/utils";
import {
	BOOKING_STATUSES,
	type BookingArt,
	type BookingStatus,
} from "@booking-hub/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Activity,
	ArrowLeft,
	Bot,
	ChevronDown,
	Clock,
	ExternalLink,
	FileText,
	Lightbulb,
	Mail,
	MessageSquare,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AITab } from "./BookingAITab";
import { ActivityTab } from "./BookingActivityTab";
import { EmailsTab } from "./BookingEmailsTab";
import { NotesTab } from "./BookingNotesTab";
import { OverviewTab } from "./BookingOverviewTab";

// ============================================================
// Booking Detail — Tabs + Status + History
// ============================================================

type Tab =
	| "overview"
	| "notes"
	| "emails"
	| "ai"
	| "history"
	| "activity"
	| "lexoffice";

export function BookingDetailPage() {
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();
	const [tab, setTab] = useState<Tab>("overview");
	const [editing, setEditing] = useState(false);
	const [formData, setFormData] = useState<Record<string, unknown>>({});

	const query = useQuery({
		queryKey: ["booking", id],
		queryFn: () =>
			api.get<{
				data: Record<string, unknown>;
				history: Array<Record<string, unknown>>;
				contact: Record<string, unknown> | null;
			}>(`/bookings/${id}`),
	});

	const updateMutation = useMutation({
		mutationFn: (data: Record<string, unknown>) =>
			api.put(`/bookings/${id}`, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["booking", id] });
			setEditing(false);
		},
	});

	const statusMutation = useMutation({
		mutationFn: (status: string) =>
			api.put(`/bookings/${id}/status`, { status }),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["booking", id] }),
	});

	const booking = query.data?.data;
	const history = query.data?.history ?? [];
	const contact = query.data?.contact ?? null;

	if (query.isLoading) {
		return (
			<div className="flex justify-center py-12">
				<div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
			</div>
		);
	}

	if (!booking) {
		return (
			<div className="text-center py-12 text-stone-400">
				Buchung nicht gefunden.
			</div>
		);
	}

	function startEditing() {
		setFormData({ ...booking });
		setEditing(true);
	}

	function setField(key: string, value: unknown) {
		// Use ?? to preserve falsy values like 0 and false
		setFormData((prev) => ({ ...prev, [key]: value ?? null }));
	}

	const tabs: { key: Tab; label: string; icon: typeof Clock }[] = [
		{ key: "overview", label: "Übersicht", icon: FileText },
		{ key: "notes", label: "Notizen", icon: MessageSquare },
		{ key: "emails", label: "E-Mails", icon: Mail },
		{ key: "ai", label: "AI", icon: Bot },
		{ key: "history", label: "Verlauf", icon: Clock },
		{ key: "activity", label: "Aktivität", icon: Activity },
		{ key: "lexoffice", label: "Belege", icon: FileText },
	];

	return (
		<div className="space-y-4 animate-fade-in">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to="/bookings" className="btn-ghost p-2">
					<ArrowLeft size={18} />
				</Link>
				<div className="flex-1">
					<h1 className="font-display text-2xl">
						{String(booking.kunde ?? "")}
					</h1>
					{booking.firma ? (
						<p className="text-sm text-stone-500">{String(booking.firma)}</p>
					) : null}
				</div>
				<StatusDropdown
					current={String(booking.status ?? "")}
					onChange={(s) => statusMutation.mutate(s)}
				/>
			</div>

			{/* Quick Info Bar */}
			<QuickInfoBar booking={booking} />

			{/* Pipeline Flow */}
			<PipelineBar booking={booking} />

			{/* AI Suggested Action Banner */}
			{booking.aiSuggestedAction ? (
				<div className="bg-brand-50 border border-brand-100 rounded-lg px-4 py-2.5 flex items-start gap-2">
					<Lightbulb size={16} className="text-brand-600 mt-0.5 shrink-0" />
					<div className="min-w-0">
						<p className="text-xs font-medium text-brand-700">AI-Empfehlung</p>
						<p className="text-sm text-brand-800">
							{String(booking.aiSuggestedAction)}
						</p>
					</div>
				</div>
			) : null}

			{/* Tabs */}
			<div className="flex gap-1 border-b border-stone-200">
				{tabs.map((t) => (
					<button
						type="button"
						key={t.key}
						onClick={() => setTab(t.key)}
						className={cn(
							"flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
							tab === t.key
								? "border-brand-600 text-brand-700"
								: "border-transparent text-stone-500 hover:text-stone-700",
						)}
					>
						<t.icon size={14} /> {t.label}
					</button>
				))}
			</div>

			{/* Tab Content */}
			{tab === "overview" && (
				<OverviewTab
					booking={booking}
					contact={contact}
					editing={editing}
					formData={formData}
					onStartEdit={startEditing}
					onSave={() => updateMutation.mutate(formData)}
					onCancel={() => setEditing(false)}
					onSetField={setField}
					saving={updateMutation.isPending}
				/>
			)}
			{tab === "notes" && <NotesTab bookingId={id ?? ""} />}
			{tab === "emails" && <EmailsTab bookingId={id ?? ""} />}
			{tab === "ai" && <AITab bookingId={id ?? ""} booking={booking} />}
			{tab === "history" && <HistoryTab history={history} />}
			{tab === "activity" && <ActivityTab bookingId={id ?? ""} />}
			{tab === "lexoffice" && <LexofficeTab booking={booking} />}
		</div>
	);
}

// ============================================================
// Completeness Bar
// ============================================================

// ============================================================
// LexOffice URL helper
// ============================================================

function lexofficeUrl(
	id: string | null | undefined,
	type: "quotation" | "orderconfirmation" | "invoice",
): string | null {
	if (!id) return null;
	const seg = {
		quotation: "quotations",
		orderconfirmation: "orderconfirmations",
		invoice: "invoices",
	};
	return `https://app.lexoffice.de/${seg[type]}/${id}`;
}

// ============================================================
// Pipeline Bar — 3-step flow (Angebot → AB → Rechnung)
// ============================================================

function PipelineBar({ booking }: { booking: Record<string, unknown> }) {
	const steps = [
		{
			label: "Angebot",
			done: !!(booking.angebotGesendet || booking.angebotsNr),
			nr: booking.angebotsNr as string | null,
			url: lexofficeUrl(booking.lexofficeAgId as string | null, "quotation"),
			color: {
				active: "bg-blue-100 text-blue-700 border-blue-200",
				dot: "bg-blue-500",
			},
		},
		{
			label: "AB",
			done: !!(booking.abGesendet || booking.abNr),
			nr: booking.abNr as string | null,
			url: lexofficeUrl(
				booking.lexofficeAbId as string | null,
				"orderconfirmation",
			),
			color: {
				active: "bg-emerald-100 text-emerald-700 border-emerald-200",
				dot: "bg-emerald-500",
			},
		},
		{
			label: "Rechnung",
			done: !!(booking.rechnungGesendet || booking.rechnungNr),
			nr: booking.rechnungNr as string | null,
			url: lexofficeUrl(booking.lexofficeInvoiceId as string | null, "invoice"),
			paid: !!booking.bezahltAm,
			color: {
				active: "bg-orange-100 text-orange-700 border-orange-200",
				dot: "bg-orange-500",
			},
		},
	];

	return (
		<div className="flex items-center gap-1.5 text-xs">
			{steps.map((s, i) => (
				<span key={s.label} className="contents">
					{i > 0 && <span className="w-4 h-px bg-stone-200 shrink-0" />}
					{s.done ? (
						<PipelineStep
							label={s.label}
							nr={s.nr}
							url={s.url}
							paid={s.paid}
							color={s.color}
						/>
					) : (
						<span className="flex items-center gap-1.5 px-2 py-1 rounded text-stone-400">
							<span className="w-2 h-2 rounded-full bg-stone-200" />
							{s.label}
						</span>
					)}
				</span>
			))}
			{!!booking.bezahltAm && (
				<>
					<span className="w-4 h-px bg-stone-200 shrink-0" />
					<span className="flex items-center gap-1.5 px-2 py-1 rounded border bg-green-100 text-green-700 border-green-200 font-medium">
						<span className="w-2 h-2 rounded-full bg-green-500" />
						Bezahlt
					</span>
				</>
			)}
		</div>
	);
}

function PipelineStep({
	label,
	nr,
	url,
	paid,
	color,
}: {
	label: string;
	nr: string | null;
	url: string | null;
	paid?: boolean;
	color: { active: string; dot: string };
}) {
	const cls = paid
		? "bg-green-100 text-green-700 border-green-200"
		: color.active;
	const dotCls = paid ? "bg-green-500" : color.dot;

	const content = (
		<span
			className={cn(
				"flex items-center gap-1.5 px-2 py-1 rounded border transition-colors",
				cls,
			)}
		>
			<span className={cn("w-2 h-2 rounded-full", dotCls)} />
			<span className="font-medium">{label}</span>
			{nr && <span className="font-mono opacity-70">{nr}</span>}
			{url && <ExternalLink size={10} className="opacity-50" />}
		</span>
	);

	if (url) {
		return (
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className="hover:opacity-80 transition-opacity"
				title={`${label} in LexOffice öffnen`}
			>
				{content}
			</a>
		);
	}
	return content;
}

// ============================================================
// Status Dropdown
// ============================================================

function StatusDropdown({
	current,
	onChange,
}: { current: string; onChange: (s: string) => void }) {
	const [open, setOpen] = useState(false);
	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className={`badge ${statusColor(current as BookingStatus)} flex items-center gap-1`}
			>
				{statusEmoji(current as BookingStatus)} {current}{" "}
				<ChevronDown size={12} />
			</button>
			{open && (
				<div className="absolute right-0 top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-stone-200 py-1 min-w-[180px]">
					{BOOKING_STATUSES.map((s) => (
						<button
							type="button"
							key={s}
							onClick={() => {
								onChange(s);
								setOpen(false);
							}}
							className={cn(
								"w-full text-left px-3 py-2 text-sm hover:bg-stone-50",
								s === current && "bg-stone-50 font-medium",
							)}
						>
							{statusEmoji(s)} {s}
						</button>
					))}
				</div>
			)}
		</div>
	);
}

// ============================================================
// History Tab
// ============================================================

function HistoryTab({ history }: { history: Array<Record<string, unknown>> }) {
	if (history.length === 0) {
		return (
			<div className="text-center py-12 text-stone-400">Noch kein Verlauf.</div>
		);
	}

	return (
		<div className="space-y-2">
			{history.map((h) => (
				<div key={String(h.id)} className="card flex items-start gap-3 !py-3">
					<Clock size={14} className="text-stone-400 mt-0.5 shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-sm">
							<span className="font-medium text-stone-700">
								{String(h.field)}
							</span>
							{h.oldValue ? (
								<span className="text-stone-400">
									{" "}
									von &quot;{String(h.oldValue)}&quot;
								</span>
							) : null}
							<span className="text-stone-400"> → </span>
							<span className="text-stone-900">
								&quot;{String(h.newValue)}&quot;
							</span>
						</p>
						<p className="text-xs text-stone-400 mt-0.5">
							{relativeTime(String(h.createdAt))} · {String(h.source)}
						</p>
					</div>
				</div>
			))}
		</div>
	);
}

// ============================================================
// LexOffice Tab
// ============================================================

// ============================================================
// Quick Info Bar — extracted to avoid unknown-as-ReactNode TS error
// ============================================================

function QuickInfoBar({ booking }: { booking: Record<string, unknown> }) {
	const datum = booking.datum ? String(booking.datum) : null;
	const personen = booking.personen ? String(booking.personen) : null;
	const art = booking.art ? String(booking.art) : null;
	const preis = booking.preis != null ? (booking.preis as number) : null;
	const quelle = booking.quelle ? String(booking.quelle) : null;

	return (
		<div className="flex flex-wrap gap-4 text-sm text-stone-600">
			{datum ? <span>📅 {formatDate(datum)}</span> : null}
			{personen ? <span>👥 {personen} Personen</span> : null}
			{art ? (
				<span className="inline-flex items-center gap-1.5">
					<span
						className="h-2 w-2 rounded-full"
						style={{ backgroundColor: artDotColor(art as BookingArt) }}
					/>
					{art}
				</span>
			) : null}
			{preis != null ? <span>💰 {formatCurrency(preis)}</span> : null}
			{quelle ? <span>📨 {quelle}</span> : null}
		</div>
	);
}

// ============================================================
// LexOffice Tab
// ============================================================

function LexofficeTab({ booking }: { booking: Record<string, unknown> }) {
	const hasAngebot = booking.angebotsNr || booking.lexofficeAgId;
	const hasAB = booking.abNr || booking.lexofficeAbId;
	const hasRechnung = booking.rechnungNr || booking.lexofficeInvoiceId;

	const rows = [
		{
			label: "Angebot",
			nr: booking.angebotsNr as string | null,
			date: booking.angebotGesendet as string | null,
			url: lexofficeUrl(booking.lexofficeAgId as string | null, "quotation"),
		},
		{
			label: "Auftragsbestätigung",
			nr: booking.abNr as string | null,
			date: booking.abGesendet as string | null,
			url: lexofficeUrl(
				booking.lexofficeAbId as string | null,
				"orderconfirmation",
			),
		},
		{
			label: "Rechnung",
			nr: booking.rechnungNr as string | null,
			date: booking.rechnungGesendet as string | null,
			url: lexofficeUrl(booking.lexofficeInvoiceId as string | null, "invoice"),
		},
	];

	if (!hasAngebot && !hasAB && !hasRechnung) {
		return (
			<div className="text-center py-12 text-stone-400">
				Noch keine Belege verknüpft.
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Pipeline Flow */}
			<PipelineBar booking={booking} />

			{/* Detail Table */}
			<div className="card !p-0 overflow-hidden">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-stone-100 bg-stone-50/50 text-left">
							<th className="px-4 py-3 font-medium text-stone-500">Beleg</th>
							<th className="px-4 py-3 font-medium text-stone-500">Nummer</th>
							<th className="px-4 py-3 font-medium text-stone-500">Datum</th>
							<th className="px-4 py-3" />
						</tr>
					</thead>
					<tbody>
						{rows
							.filter((r) => r.nr || r.url)
							.map((r) => (
								<tr key={r.label} className="border-b border-stone-50">
									<td className="px-4 py-3 font-medium">{r.label}</td>
									<td className="px-4 py-3 font-mono text-xs">{r.nr ?? "—"}</td>
									<td className="px-4 py-3 text-stone-600">
										{formatDate(r.date)}
									</td>
									<td className="px-4 py-3">
										{r.url && (
											<a
												href={r.url}
												target="_blank"
												rel="noopener noreferrer"
												className="text-stone-400 hover:text-brand-600 transition-colors"
												title="In LexOffice öffnen"
											>
												<ExternalLink size={14} />
											</a>
										)}
									</td>
								</tr>
							))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
